import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { translate } from '@jsverse/transloco';

export interface ChunkSection {
  key: string;
  failedItems: Array<{ rows?: number; error_message?: string; status?: string }>;
}

export interface StatusSummaryItem {
  item: string;
  rows: number;
  status: string;
  error?: string;
}

@Component({
  selector: 'app-import-file-modal',
  templateUrl: './import-file-modal.component.html',
  styleUrl: './import-file-modal.component.sass',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImportFileModalComponent {

  readonly errorDisplayedColumns: string[] = ['row', 'error_message', 'status'];
  readonly statusDisplayedColumns: string[] = ['item', 'rows', 'status'];

  constructor(private dialogRef: MatDialogRef<ImportFileModalComponent>, @Inject(MAT_DIALOG_DATA) public data: any) { }

  get modalMode(): 'errors' | 'status-summary' {
    const status = this.data?.status?.toLowerCase();
    if (status === 'partially-failed') {
      return 'errors';
    }
    if (status === 'success' || status === 'failed') {
      return 'status-summary';
    }
    return 'errors';
  }

  get modalTitle(): string {
    return this.modalMode === 'errors' 
      ? translate('imports.errors') 
      : translate('imports.import_details_title');
  }

  get chunkSections(): ChunkSection[] {
    const chunks = this.data?.metadata?.chunks;
    if (!chunks) return [];

    if (Array.isArray(chunks)) {
      const failedItems = chunks.filter((i: any) => i?.status === 'failed');
      return failedItems.length > 0 ? [{ key: 'Errors', failedItems }] : [];
    }

    return Object.entries(chunks)
      .map(([key, items]) => {
        const itemsArray = Array.isArray(items) 
          ? items 
          : Object.values(items as Record<string, any>);
        return {
          key,
          failedItems: itemsArray.filter((i: any) => i?.status === 'failed')
        };
      })
      .filter(section => section.failedItems.length > 0);
  }

  get statusSummaryItems(): StatusSummaryItem[] {
    const chunks = this.data?.metadata?.chunks;
    if (chunks) {
      return Object.entries(chunks).map(([sheetName, chunkItems]: [string, any]) => {
        const itemsArray: any[] = Array.isArray(chunkItems) 
          ? chunkItems 
          : Object.values(chunkItems);
        
        const totalRows = itemsArray.reduce((sum, chunk) => sum + (chunk.rows || 0), 0);
        const hasFailed = itemsArray.some(chunk => chunk.status === 'failed');
        const allSuccess = itemsArray.every(chunk => chunk.status === 'success');
        const errorMessage = itemsArray.find(chunk => chunk.error_message)?.error_message || '';
        
        return {
          item: sheetName,
          rows: totalRows,
          status: hasFailed ? 'failed' : allSuccess ? 'success' : 'warning',
          error: errorMessage
        };
      });
    }

    return this.geoJsonStatusSummaryItems;
  }

  /** Failed/success GeoJSON jobs use metadata.message / .error / .errors / .stored_features */
  get geoJsonStatusSummaryItems(): StatusSummaryItem[] {
    const meta = this.data?.metadata;
    if (!meta || typeof meta !== 'object') {
      return [];
    }
    const type = (this.data?.type || '').toLowerCase();
    const looksLikeGeoJsonMeta =
      type === 'geojson' ||
      meta.error != null ||
      meta.errors != null ||
      meta.stored_features != null ||
      (typeof meta.message === 'string' && /geojson/i.test(meta.message));

    if (!looksLikeGeoJsonMeta) {
      return [];
    }

    const status = (this.data?.status || '').toLowerCase();
    if (status === 'success') {
      return [
        {
          item: this.data?.name || 'GeoJSON',
          rows: typeof meta.stored_features === 'number' ? meta.stored_features : 0,
          status: 'success',
        },
      ];
    }

    if (status === 'failed') {
      let errorText = '';
      if (typeof meta.error === 'string' && meta.error) {
        errorText = meta.error;
      } else if (Array.isArray(meta.errors) && meta.errors.length) {
        errorText = meta.errors
          .map((e: any) => (typeof e === 'string' ? e : e?.errors || e?.message || JSON.stringify(e)))
          .join('; ');
      } else if (typeof meta.message === 'string') {
        errorText = meta.message;
      }
      return [
        {
          item: this.data?.name || 'GeoJSON',
          rows: meta.failed_index != null ? Number(meta.failed_index) + 1 : 0,
          status: 'failed',
          error: errorText,
        },
      ];
    }

    return [];
  }

  get showErrorsTable(): boolean {
    return this.modalMode === 'errors' && this.chunkSections.length > 0;
  }

  get showStatusSummary(): boolean {
    return this.modalMode === 'status-summary' && this.statusSummaryItems.length > 0;
  }

  get showNoDataMessage(): boolean {
    if (this.modalMode === 'errors') {
      return !this.showErrorsTable;
    }
    return !this.showStatusSummary;
  }

  get showGeoJsonErrorDetail(): boolean {
    return (
      this.modalMode === 'status-summary' &&
      this.geoJsonStatusSummaryItems.some((i) => i.status === 'failed' && !!i.error)
    );
  }

  getDisplayErrorMessage(errorMessage: string | undefined): string {
    if (errorMessage == null || errorMessage === '') return '-';
    return errorMessage.includes('SQLSTATE') ? translate('imports.problem_in_server') : errorMessage;
  }

  getChunkLabelKey(key: string): string {
    const map: Record<string, string> = {
      stakeholders: 'stakeholders.labels',
      issues: 'issues.labels',
      contactmomenten: 'contactmoments.labels',
      contactmoments: 'contactmoments.labels',
      middelen: 'contents.labels',
      contents: 'contents.labels',
      vergunningen: 'permits.labels',
      permits: 'permits.labels',
      contactpersonen: 'contactpersons.labels',
      contactpersons: 'contactpersons.labels',
      meldingen: 'complaints.labels',
      complaints: 'complaints.labels',
      activiteiten: 'activities.labels',
      activities: 'activities.labels',
      klanteisen: 'requirements.labels',
      requirements: 'requirements.labels'
    };
    return translate(map[key] ?? key);
  }

  close() {
    this.dialogRef.close();
  }
}
